import { z } from 'zod';
import type { BaseplanCode, CodeBoard } from '@/types/oscar';

/**
 * Person create/edit form schema (PR-04). Mirrors the server-side validation
 * in PersonsFunctions: DisplayName/FirstName/LastName/PrimaryEmail required,
 * email well-formed, AccountStatus constrained. Optional text fields are kept
 * as strings; the API maps empty strings to NULL.
 */
export const accountStatuses = [
  'Active',
  'Onboarding',
  'Offboarding',
  'Terminated',
  'OnLeave',
] as const;

export const personSchema = z.object({
  EmployeeId: z.string().trim().optional(),
  DisplayName: z.string().trim().min(1, 'Display name is required'),
  PreferredName: z.string().trim().optional(),
  FirstName: z.string().trim().min(1, 'First name is required'),
  LastName: z.string().trim().min(1, 'Last name is required'),
  PrimaryEmail: z.string().trim().min(1, 'Primary email is required').email('Enter a valid email address'),
  LocationId: z.number().int().positive().nullable().optional(),
  CostCentreCode: z.string().trim().optional(),
  JobTitle: z.string().trim().optional(),
  Department: z.string().trim().optional(),
  // Derived from the selected Position Title — not directly editable. Kept in
  // the form so a picked job title can carry its department through to the API.
  DepartmentCode: z.string().trim().nullable().optional(),
  ManagerPersonId: z.number().int().positive().nullable().optional(),
  Mobile: z.string().trim().optional(),
  /** Work landline (OnsiteCore-oscar-Person-Phone.sql); copied to OnsiteLive, whose column is 50 wide. */
  Phone: z.string().trim().max(50, 'At most 50 characters').optional(),
  Username: z.string().trim().optional(),
  EmploymentStartDate: z.string().trim().optional(),
  /** The Baseplan code the person belongs to; the person sync copies it to OnsiteLive (VARCHAR(20)). */
  HomeBranchCode: z.string().trim().max(20, 'At most 20 characters').optional(),
  AccountStatus: z.enum(accountStatuses),
  Notes: z.string().trim().optional(),
});

export type PersonFormData = z.infer<typeof personSchema>;

/**
 * The primary-branch options: the location's branches first (a place can run
 * several - team walk-through 26/10/2026), then every active code by area. A current
 * code that is not on the board (the first seed copied OnsiteLive's branch as it
 * was) is still listed, so opening the form never blanks it.
 */
export function homeBranchGroups(
  board: CodeBoard | undefined, current: string, locationCodes: readonly string[],
): { label: string; codes: Pick<BaseplanCode, 'Code' | 'Description'>[] }[] {
  const codes = (board?.Codes ?? []).filter((c) => c.IsActive);
  const groups: { label: string; codes: Pick<BaseplanCode, 'Code' | 'Description'>[] }[] = [];
  const suggested = locationCodes
    .map((code) => codes.find((c) => c.Code === code))
    .filter((c): c is BaseplanCode => c !== undefined);
  if (suggested.length > 0) groups.push({ label: "This location's branches", codes: suggested });
  if (current && !codes.some((c) => c.Code === current))
    groups.push({ label: 'Current (not on the board)', codes: [{ Code: current, Description: '' }] });
  const areaName = new Map((board?.Areas ?? []).map((a) => [a.AreaId, a.AreaDesc] as const));
  const byArea = new Map<string, Pick<BaseplanCode, 'Code' | 'Description'>[]>();
  for (const c of codes) {
    if (suggested.includes(c)) continue;
    const label = (c.AreaId != null ? areaName.get(c.AreaId) : undefined) ?? 'No area';
    byArea.set(label, [...(byArea.get(label) ?? []), c]);
  }
  [...byArea.keys()]
    .sort((a, b) => (a === 'No area' ? 1 : 0) - (b === 'No area' ? 1 : 0) || a.localeCompare(b))
    .forEach((label) => groups.push({ label, codes: byArea.get(label)!.sort((x, y) => x.Code.localeCompare(y.Code)) }));
  return groups;
}
